import { Check, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import type { MatchCard, Need, Service } from "@hub/shared";
import { Button, IconButton, Menu, Pill, relativeTime } from "@/ui";
import { MatchRow } from "./MatchRow";
import s from "../network.module.css";

const VISIBILITY_LABEL = { private: "Private", connections: "My connections", network: "Hub network" } as const;

export function NeedRow({ need, ownerName, matches, onEdit, onComplete, onDelete, onOpenMatch, onMatchAction }: { need: Need; ownerName?: string | null; matches?: MatchCard[]; onEdit: () => void; onComplete: () => void; onDelete: () => void; onOpenMatch?: (c: MatchCard) => void; onMatchAction?: (c: MatchCard, a: MatchCard["actions"][number]) => void }) {
  return (
    <div className={s.itemRow} data-done={need.isCompleted ? "true" : undefined} data-testid={`need-${need.id}`}>
      <div style={{ minWidth: 0 }}>
        <div className={s.itemTitle}>
          <span>{need.title}</span>
          {need.isCompleted ? <Pill tone="success">Completed</Pill> : <Pill tone="neutral">{VISIBILITY_LABEL[need.visibility]}</Pill>}
          {ownerName && <span className={`${s.small} ${s.muted}`}>{ownerName}</span>}
        </div>
        {need.description && <div className={s.itemDesc}>{need.description}</div>}
        <div className={s.chips}>
          <span className={s.chip}>{need.category}</span>
          {need.timeline && <span className={s.chip}>{need.timeline}</span>}
          {need.location && <span className={s.chip}>{need.location}</span>}
          {need.keywords.slice(0, 6).map((k) => (
            <span key={k} className={`${s.chip} ${s.muted}`}>
              {k}
            </span>
          ))}
        </div>
        <div className={`${s.small} ${s.muted}`} style={{ marginTop: 4 }}>
          {need.isCompleted && need.completedAt ? `Completed ${relativeTime(need.completedAt)}` : `Updated ${relativeTime(need.updated)}`}
        </div>
        {matches && matches.length > 0 && onOpenMatch && onMatchAction && (
          <div className="u-col" style={{ gap: 6, marginTop: 8 }}>
            <div className={s.blockLabel}>Matches for this need</div>
            {matches.map((c) => (
              <MatchRow key={c.matchId} card={c} compact onOpen={onOpenMatch} onAction={onMatchAction} />
            ))}
          </div>
        )}
      </div>
      <div className="u-row" style={{ alignItems: "flex-start" }}>
        {!need.isCompleted && (
          <Button size="sm" variant="outline" icon={<Check size={14} />} onClick={onComplete}>
            Complete
          </Button>
        )}
        <Menu
          align="right"
          trigger={
            <IconButton label="Need options">
              <MoreHorizontal size={16} />
            </IconButton>
          }
          items={[
            { id: "edit", label: "Edit", icon: <Pencil size={14} />, onSelect: onEdit },
            { id: "d", label: "", divider: true },
            { id: "delete", label: "Delete", icon: <Trash2 size={14} />, danger: true, onSelect: onDelete },
          ]}
        />
      </div>
    </div>
  );
}

export function ServiceRow({ service, ownerName, matches, onEdit, onToggleActive, onDelete, onOpenMatch, onMatchAction }: { service: Service; ownerName?: string | null; matches?: MatchCard[]; onEdit: () => void; onToggleActive: () => void; onDelete: () => void; onOpenMatch?: (c: MatchCard) => void; onMatchAction?: (c: MatchCard, a: MatchCard["actions"][number]) => void }) {
  return (
    <div className={s.itemRow} data-done={service.isActive ? undefined : "true"} data-testid={`service-${service.id}`}>
      <div style={{ minWidth: 0 }}>
        <div className={s.itemTitle}>
          <span>{service.title}</span>
          {service.isActive ? <Pill tone="neutral">{VISIBILITY_LABEL[service.visibility]}</Pill> : <Pill tone="warning">Paused</Pill>}
          {ownerName && <span className={`${s.small} ${s.muted}`}>{ownerName}</span>}
        </div>
        {service.shortDescription && <div className={s.itemDesc}>{service.shortDescription}</div>}
        <div className={s.chips}>
          <span className={s.chip}>{service.category}</span>
          {service.location && <span className={s.chip}>{service.location}</span>}
          {service.keywords.slice(0, 6).map((k) => (
            <span key={k} className={`${s.chip} ${s.muted}`}>
              {k}
            </span>
          ))}
        </div>
        {matches && matches.length > 0 && onOpenMatch && onMatchAction && (
          <div className="u-col" style={{ gap: 6, marginTop: 8 }}>
            <div className={s.blockLabel}>People this could help</div>
            {matches.map((c) => (
              <MatchRow key={c.matchId} card={c} compact onOpen={onOpenMatch} onAction={onMatchAction} />
            ))}
          </div>
        )}
      </div>
      <div className="u-row" style={{ alignItems: "flex-start" }}>
        <Menu
          align="right"
          trigger={
            <IconButton label="Offer options">
              <MoreHorizontal size={16} />
            </IconButton>
          }
          items={[
            { id: "edit", label: "Edit", icon: <Pencil size={14} />, onSelect: onEdit },
            { id: "toggle", label: service.isActive ? "Pause" : "Resume", onSelect: onToggleActive },
            { id: "d", label: "", divider: true },
            { id: "delete", label: "Delete", icon: <Trash2 size={14} />, danger: true, onSelect: onDelete },
          ]}
        />
      </div>
    </div>
  );
}
